import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  FileText, CheckCircle, Clock, Download, Loader2, Shield, Lock,
  PenTool, AlertCircle, Building2, Calendar, Hash, ListChecks, Image as ImageIcon, Trash2, Upload,
  ShieldCheck,
} from 'lucide-react';
import { generateActaPDF } from '../utils/actaPdf';
import CertSignaturePanel from '../components/CertSignaturePanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_rapid-systems/artifacts/cv8q3bmw_LOGO%20ANALYTICA.png";

const STATUS_COLORS = {
  passed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  blocked: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-blue-100 text-blue-700',
  pending: 'bg-gray-100 text-gray-700',
};
const STATUS_LABEL = {
  passed: 'APROBADO', failed: 'FALLIDO', blocked: 'BLOQUEADO',
  in_progress: 'EN CURSO', pending: 'PENDIENTE',
};

const FirmarActaPage = () => {
  const { token } = useParams();
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const [data, setData] = useState(null); // { okr, plan, backlogs, cases }
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signatureMode, setSignatureMode] = useState('draw');
  const [uploadedSignature, setUploadedSignature] = useState(null);
  const [clientName, setClientName] = useState('');
  const [comment, setComment] = useState('');
  // Tipo de firma: 'simple' (canvas/imagen) o 'cert' (PAdES con .p12)
  const [signatureType, setSignatureType] = useState('simple');
  const [certData, setCertData] = useState(null); // { p12_base64, password, info }
  const [signingWithCert, setSigningWithCert] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/qa/acta/token/${token}`);
        setData(r.data);
        const cn = r.data?.plan?.client_company_name || '';
        setClientName(cn);
      } catch (e) {
        toast.error('Acta no encontrada o link invalido');
      } finally { setLoading(false); }
    })();
  }, [token]);

  const okr = data?.okr;
  const plan = data?.plan;
  const backlogs = data?.backlogs || [];
  const canSign = okr && ['sent', 'draft'].includes(okr.acta_status);

  useEffect(() => {
    if (canvasRef.current && canSign && signatureMode === 'draw') {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#2F3D8E';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [canSign, signatureMode]);

  const startDrawing = (e) => {
    if (!canSign || signatureMode !== 'draw') return;
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    ctx.lineTo(x, y); ctx.stroke();
    setHasSignature(true);
  };
  const stopDrawing = () => setIsDrawing(false);
  const clearSig = () => {
    if (signatureMode === 'draw') {
      const c = canvasRef.current;
      if (c) {
        const ctx = c.getContext('2d');
        ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height);
      }
    } else {
      setUploadedSignature(null);
    }
    setHasSignature(false);
  };
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Imagen invalida'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Maximo 5MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => { setUploadedSignature(ev.target.result); setHasSignature(true); };
    reader.readAsDataURL(file);
  };

  const sign = async () => {
    if (!hasSignature) { toast.error('Proporcione su firma'); return; }
    setSigning(true);
    try {
      let signatureImage;
      if (signatureMode === 'draw') signatureImage = canvasRef.current.toDataURL('image/png');
      else signatureImage = uploadedSignature;
      await axios.post(`${API}/qa/acta/token/${token}/sign-client`, {
        client_signature_data_url: signatureImage,
        client_name: clientName || (plan?.client_company_name || 'Cliente'),
        comment,
      });
      toast.success('Acta firmada con exito');
      const r = await axios.get(`${API}/qa/acta/token/${token}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al firmar');
    } finally { setSigning(false); }
  };

  const signWithCert = async () => {
    if (!certData) { toast.error('Cargue y valide su certificado primero'); return; }
    setSigningWithCert(true);
    try {
      // 1) Generar el PDF del acta y convertir a base64
      const doc = await generateActaPDF(okr, plan, backlogs, { download: false });
      const dataUri = doc.output('datauristring');
      const pdfBase64 = dataUri.split(',', 2)[1];
      // 2) Llamar al endpoint PAdES
      const r = await axios.post(`${API}/signatures/acta/token/${token}/sign-with-cert`, {
        pdf_base64: pdfBase64,
        p12_base64: certData.p12_base64,
        password: certData.password,
        client_name: clientName || certData.info?.common_name,
        comment,
        reason: 'Aceptacion del acta de pruebas QA',
        location: 'Ecuador',
      });
      toast.success(`Acta firmada con certificado · ${r.data.cert_info?.common_name}`);
      const refresh = await axios.get(`${API}/qa/acta/token/${token}`);
      setData(refresh.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al firmar con certificado');
    } finally { setSigningWithCert(false); }
  };

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      await generateActaPDF(okr, plan, backlogs, { download: true });
      toast.success('PDF descargado');
    } catch (e) { toast.error('Error al generar PDF'); }
    finally { setDownloading(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100"><Loader2 className="w-12 h-12 animate-spin text-[#2F3D8E]" /></div>;
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800">Acta no encontrada</h1>
          <p className="text-gray-600 mt-2">El enlace puede haber expirado o ser invalido.</p>
        </div>
      </div>
    );
  }

  const total = backlogs.length;
  const passed = backlogs.filter(c => c.status === 'passed').length;
  const failed = backlogs.filter(c => c.status === 'failed').length;
  const blocked = backlogs.filter(c => c.status === 'blocked').length;
  const passPct = total > 0 ? (passed / total * 100).toFixed(1) : 0;

  const status = okr.acta_status;
  const statusInfo =
    status === 'completed' ? { color: 'green', icon: CheckCircle, text: 'Completada', desc: 'Ambas firmas han sido registradas.' } :
    status === 'client_signed' ? { color: 'yellow', icon: Clock, text: 'Firmada por Cliente', desc: 'Tu firma fue recibida. Esperando firma de Analytica.' } :
    { color: 'blue', icon: FileText, text: 'Pendiente de Firma', desc: 'El acta esta lista para revision y firma.' };
  const StatusIcon = statusInfo.icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="Analytica" className="h-12" />
          <div className="flex items-center gap-2 text-gray-600">
            <Shield className="w-5 h-5 text-[#4C8C3B]" />
            <span className="text-sm font-medium">Firma Electronica Segura</span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Cover */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6" data-testid="acta-cover">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <p className="text-sm text-gray-500">Acta de Aceptacion QA</p>
              <h1 className="text-2xl font-bold text-[#2F3D8E]">{okr.code} · {plan.plan_number}</h1>
              <p className="text-gray-700 mt-1 font-medium">{okr.objective}</p>
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full bg-${statusInfo.color}-100`}>
              <StatusIcon className={`w-5 h-5 text-${statusInfo.color}-600`} />
              <span className={`font-medium text-${statusInfo.color}-700`}>{statusInfo.text}</span>
            </div>
          </div>
          <p className="text-gray-600 mt-2 text-sm">{statusInfo.desc}</p>
        </div>

        {/* Cliente */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-lg font-bold text-[#2F3D8E] mb-4">Resumen</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-gray-400" />
                <div><p className="text-xs text-gray-500">Cliente</p><p className="font-medium">{plan.client_company_name}</p></div>
              </div>
              {plan.contract_number && (
                <div className="flex items-center gap-3">
                  <Hash className="w-5 h-5 text-gray-400" />
                  <div><p className="text-xs text-gray-500">Contrato</p><p className="font-medium">{plan.contract_number}</p></div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <ListChecks className="w-5 h-5 text-gray-400" />
                <div><p className="text-xs text-gray-500">Backlogs</p><p className="font-medium">{total} backlogs · {passPct}% aprobados</p></div>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="bg-blue-50 rounded p-2"><div className="text-lg font-bold text-blue-700">{total}</div><div className="text-[10px] text-blue-600">TOTAL</div></div>
              <div className="bg-green-50 rounded p-2"><div className="text-lg font-bold text-green-700">{passed}</div><div className="text-[10px] text-green-600">PASS</div></div>
              <div className="bg-red-50 rounded p-2"><div className="text-lg font-bold text-red-700">{failed}</div><div className="text-[10px] text-red-600">FAIL</div></div>
              <div className="bg-amber-50 rounded p-2"><div className="text-lg font-bold text-amber-700">{blocked}</div><div className="text-[10px] text-amber-600">BLOCK</div></div>
            </div>
          </div>

          {okr.key_results && okr.key_results.length > 0 && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs uppercase font-semibold text-blue-700 mb-1.5">Resultados Clave</p>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                {okr.key_results.map((kr, i) => <li key={i}>{kr}</li>)}
              </ul>
            </div>
          )}

          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={downloadPDF} disabled={downloading} data-testid="download-acta-pdf-btn">
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Descargar PDF del Acta
            </Button>
          </div>
        </div>

        {/* Backlogs detalle */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6" data-testid="acta-backlogs">
          <h2 className="text-lg font-bold text-[#2F3D8E] mb-4">Detalle de Backlogs</h2>
          {backlogs.length === 0 && <p className="text-sm text-gray-500">Sin backlogs asociados a este OKR.</p>}
          <div className="space-y-3">
            {backlogs.map(b => {
              const stCls = STATUS_COLORS[b.status] || STATUS_COLORS.pending;
              const doneCount = (b.checklist || []).filter(c => c.done).length;
              return (
                <div key={b.id} className="border rounded-lg p-3" data-testid={`acta-backlog-${b.code}`}>
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div className="flex-1 min-w-[60%]">
                      <div className="font-semibold text-[#1a2456]"><span className="font-mono text-xs text-gray-500 mr-2">{b.code}</span>{b.title}</div>
                      {b.requirement && <div className="text-xs text-gray-500 mt-0.5">Requisito: {b.requirement}</div>}
                      {b.description && <p className="text-xs text-gray-600 mt-1">{b.description}</p>}
                      {b.due_date && <div className="text-xs text-gray-500 mt-0.5">Vence: {b.due_date}</div>}
                    </div>
                    <span className={`text-[10px] px-2 py-1 rounded-full ${stCls}`}>{STATUS_LABEL[b.status] || b.status}</span>
                  </div>
                  {b.last_note && <p className="text-xs italic text-gray-500 mt-2">Obs: {b.last_note}</p>}
                  {b.checklist && b.checklist.length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Checklist ({doneCount}/{b.checklist.length})</div>
                      <div className="space-y-1">
                        {b.checklist.map((ci, i) => (
                          <div key={ci.id || i} className={`flex items-center gap-2 text-xs ${ci.done ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                            <span className={`w-3 h-3 rounded border-2 flex-shrink-0 ${ci.done ? 'bg-green-500 border-green-500' : 'border-gray-300'}`}></span>
                            <span className="flex-1">{ci.title}</span>
                            {ci.assigned_to && <span className="text-[10px] text-gray-500">{ci.assigned_to}</span>}
                            {ci.due_date && <span className="text-[10px] text-gray-500">{ci.due_date}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {b.comments && b.comments.length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Comentarios</div>
                      <div className="space-y-1">
                        {b.comments.map((c, i) => (
                          <div key={c.id || i} className="bg-gray-50 rounded p-2 text-xs">
                            <div className="font-semibold text-[#2F3D8E]">{c.author} <span className="font-normal text-gray-400">· {new Date(c.created_at).toLocaleString('es-EC')}</span></div>
                            <div className="text-gray-700">{c.text}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {b.evidence && b.evidence.length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Evidencias</div>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {b.evidence.map((ev, i) => (
                          <div key={ev.id || i} className="relative">
                            {ev.data_url ? (
                              <a href={ev.data_url} target="_blank" rel="noreferrer">
                                <img src={ev.data_url} alt={ev.filename} className="w-full h-20 object-cover rounded border" />
                              </a>
                            ) : (
                              <div className="w-full h-20 bg-gray-100 rounded border flex items-center justify-center text-gray-400 text-xs">sin imagen</div>
                            )}
                            {ev.note && <div className="text-[10px] text-gray-500 truncate" title={ev.note}>{ev.note}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Signature Section */}
        {canSign && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-lg font-bold text-[#2F3D8E] mb-4 flex items-center gap-2">
              <PenTool className="w-5 h-5" />
              Firmar Acta
            </h2>

            <div className="grid md:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-xs text-gray-600">Nombre del firmante</label>
                <Input value={clientName} onChange={e => setClientName(e.target.value)} data-testid="acta-client-name-input" />
              </div>
              <div>
                <label className="text-xs text-gray-600">Comentario (opcional)</label>
                <Input value={comment} onChange={e => setComment(e.target.value)} placeholder="Observacion" />
              </div>
            </div>

            {/* Toggle tipo de firma */}
            <div className="flex gap-2 mb-4 border-b">
              <button onClick={() => setSignatureType('simple')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${signatureType === 'simple' ? 'border-[#2F3D8E] text-[#2F3D8E]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                      data-testid="tab-firma-simple">
                Firma Electronica Simple
              </button>
              <button onClick={() => setSignatureType('cert')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${signatureType === 'cert' ? 'border-[#2F3D8E] text-[#2F3D8E]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                      data-testid="tab-firma-cert">
                Firma con Certificado (PAdES) <span className="ml-1 text-[10px] bg-[#4C8C3B] text-white px-1.5 py-0.5 rounded-full">avanzada</span>
              </button>
            </div>

            {signatureType === 'simple' ? (
              <>
                <div className="flex gap-2 mb-4">
                  <Button variant={signatureMode === 'draw' ? 'default' : 'outline'}
                          onClick={() => { setSignatureMode('draw'); clearSig(); }}
                          className={signatureMode === 'draw' ? 'bg-[#2F3D8E]' : ''}>
                    <PenTool className="w-4 h-4 mr-2" /> Dibujar
                  </Button>
                  <Button variant={signatureMode === 'upload' ? 'default' : 'outline'}
                          onClick={() => { setSignatureMode('upload'); clearSig(); }}
                          className={signatureMode === 'upload' ? 'bg-[#2F3D8E]' : ''}>
                    <Upload className="w-4 h-4 mr-2" /> Subir Imagen
                  </Button>
                </div>

                {signatureMode === 'draw' ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-2 mb-4">
                    <canvas ref={canvasRef} width={600} height={200}
                            className="w-full bg-white rounded-lg cursor-crosshair touch-none"
                            onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing}
                            onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} />
                  </div>
                ) : (
                  <>
                    <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="image/*" className="hidden" />
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 mb-4 text-center cursor-pointer hover:border-[#2F3D8E]"
                         onClick={() => fileInputRef.current?.click()}>
                      {uploadedSignature ? (
                        <img src={uploadedSignature} alt="firma" className="max-h-32 mx-auto rounded-lg border" />
                      ) : (
                        <div className="space-y-2 text-gray-500">
                          <ImageIcon className="w-10 h-10 mx-auto" />
                          <p>Haga clic para seleccionar imagen</p>
                          <p className="text-xs">PNG, JPG (max 5MB)</p>
                        </div>
                      )}
                    </div>
                  </>
                )}

                <div className="flex gap-4 mb-4">
                  <Button variant="outline" onClick={clearSig} disabled={!hasSignature}>
                    <Trash2 className="w-4 h-4 mr-2" /> Limpiar
                  </Button>
                  <Button className="bg-[#4C8C3B] hover:bg-[#3d7030] flex-1" onClick={sign}
                          disabled={!hasSignature || signing} data-testid="sign-acta-btn">
                    {signing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Firmando...</> :
                              <><PenTool className="w-4 h-4 mr-2" /> Firmar Acta</>}
                  </Button>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                  <p className="flex items-center gap-2"><Lock className="w-4 h-4" /> Su firma sera registrada de forma segura junto con fecha y hora.</p>
                </div>
              </>
            ) : (
              <>
                <CertSignaturePanel
                  onReady={(p12_base64, password, info) => setCertData({ p12_base64, password, info })}
                  onClear={() => setCertData(null)}
                />
                <Button className="bg-[#4C8C3B] hover:bg-[#3d7030] w-full mt-4" onClick={signWithCert}
                        disabled={!certData || signingWithCert} data-testid="sign-acta-cert-btn">
                  {signingWithCert ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Firmando PAdES...</> :
                                     <><ShieldCheck className="w-4 h-4 mr-2" /> Firmar acta con certificado</>}
                </Button>
              </>
            )}
          </div>
        )}

        {/* Estados firmados */}
        {okr.acta_client_signed_at && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-6">
            <h2 className="text-lg font-bold text-green-700 mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" /> Acta firmada por el cliente
            </h2>
            <div className="text-sm text-gray-700 space-y-1">
              <p><Calendar className="w-4 h-4 inline mr-1 text-green-600" />Fecha: {new Date(okr.acta_client_signed_at).toLocaleString('es-EC')}</p>
              {okr.acta_client_name && <p>Firmante: <strong>{okr.acta_client_name}</strong></p>}
              {okr.acta_client_comment && <p className="italic text-gray-600">Comentario: {okr.acta_client_comment}</p>}
            </div>
            {okr.acta_client_signature_data_url && (
              <div className="mt-3 p-3 bg-white rounded-lg border inline-block">
                <p className="text-xs text-gray-500 mb-1">Firma:</p>
                <img src={okr.acta_client_signature_data_url} alt="firma cliente" className="max-h-24" />
              </div>
            )}
          </div>
        )}

        {okr.acta_status === 'completed' && okr.acta_analytica_signed_at && (
          <div className="bg-[#2F3D8E] text-white rounded-2xl p-6 mb-6">
            <h2 className="text-lg font-bold mb-3 flex items-center gap-2"><Shield className="w-5 h-5" /> Acta completada</h2>
            <p className="text-sm">Firmada por Analytica el {new Date(okr.acta_analytica_signed_at).toLocaleString('es-EC')}</p>
            {okr.acta_analytica_signature_data_url && (
              <div className="mt-3 p-3 bg-white rounded-lg border inline-block">
                <img src={okr.acta_analytica_signature_data_url} alt="firma analytica" className="max-h-24" />
              </div>
            )}
            <Button className="mt-4 bg-[#4C8C3B] hover:bg-[#3d7030]" onClick={downloadPDF} disabled={downloading}>
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Descargar Acta Firmada
            </Button>
          </div>
        )}

        <div className="text-center text-sm text-gray-500">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="w-4 h-4" />
            <span>Documento protegido con firma electronica</span>
          </div>
          <p>© {new Date().getFullYear()} Analytica - Todos los derechos reservados</p>
        </div>
      </main>
    </div>
  );
};

export default FirmarActaPage;
